/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import DatePickerOutline from 'components/MDDatePicker/DatePickerOutline';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  pr: 'ALL',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL',
  branch: 'ALL',
  date_record: Func.dateNow(),
};

function SearchExport() {
  const mod = 'dashboard_stock';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [find, setFind] = useState(initFind);
  const [summary, setSummary] = useState({});
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboStepper, setCboStepper] = useState([]);
  const txtSearch = useRef(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);

    FetchData('POST', `/${mod}/viewexport`, app.token, {
      lang: app.lang,
      search: data.search,
      ref_id: id,
      branch: data.branch,
      status: data.status,
      date_record: data.date_record,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const init = () => {
    FetchData('POST', `/${mod}/initexport`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboStepper(result.stepper);
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const open = () => {
    setModalAdd(true);
    view(find);

    setTimeout(function () {
      txtSearch.current.focus();
    }, 500);
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value })}
            >
              <MenuItem value='ALL'>สาขา</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value })}
            >
              <MenuItem value='ALL'>สถานะ</MenuItem>
              {cboStepper.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <DatePickerOutline
              onChange={([date]) => {
                view({ ...find, date_record: Func.dateFormat(date) });
              }}
              value={new Date(find.date_record)}
              label='วันที่'
              input={{
                variant: 'outlined',
                fullWidth: true,
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox sx={{ minHeight: 600 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {record.map((row) => (
                <Grid item xs={12} sm={12} md={6} lg={3} key={row.id}>
                  <MDBox className='card' sx={{ overflow: 'hidden' }}>
                    <CardMedia
                      sx={{ height: 230, m: 0, borderRadius: 0 }}
                      image={row.image}
                      title={row.code}
                    />
                    <CardHeader
                      avatar={
                        <Tooltip title={row.status_name}>
                          <Alert
                            icon={<Icon>{row.status_icon}</Icon>}
                            severity={row.status_color}
                            sx={{
                              fontSize: '14px',
                              padding: '0 10px',
                              fontWeight: 400,
                              width: 40,
                              borderRadius: 5,
                            }}
                          />
                        </Tooltip>
                      }
                      title={row.code}
                      subheader={row.branch_name}
                      action={<IconButton aria-label='box'>{row.box}</IconButton>}
                    />
                    <CardContent
                      sx={{ px: 2, py: 0, fontSize: 15, color: '#666666', fontWeight: 100 }}
                    >
                      <Grid container>
                        <Grid item xs={6}>
                          ใบขอโอนสินค้า
                        </Grid>
                        <Grid item xs={6} className='text-right color-info'>
                          {row.request_code}
                        </Grid>
                        <Grid item xs={6}>
                          วันที่
                        </Grid>
                        <Grid item xs={6} className='text-right'>
                          {Func.dateTimeDisplay(row.date_approve)}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderSummary = () => {};

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          รายการโอนย้าย
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
        <DialogActions>
          <Chip
            sx={{ pl: 1.5 }}
            icon={<Icon>inventory_2</Icon>}
            color='warning'
            variant='outlined'
            label={Func.numberDisplay(summary.count_data)}
          />
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <div>
      <MDButton
        size='small'
        sx={{
          fontSize: '0.875rem',
          display: 'inline-block',
          textAlign: 'left',
          minHeight: 'auto',
          p: 0,
          m: 0,
          fontWeight: 'normal',
        }}
        onClick={open}
      >
        แสดงรายละเอียด <Icon color='primary'>launch</Icon>
      </MDButton>

      {renderAdd()}
      {renderLoading()}
      {notify}
    </div>
  );
}

export default SearchExport;
